/**
 * Confirm dialog (idc-portal-design v1.1) for consequential, hard-to-undo actions (Disconnect Outlook): a native
 * <dialog> opened with showModal() (inert background, top layer above the SDK bar), the safe choice first and
 * focused, Esc and a backdrop click cancel, and focus goes back to the trigger on cancel. On confirm too (AIC-318):
 * when the dialog closes and focus was left nowhere (the body, or the closed dialog), it goes back to the trigger if
 * that is still on the page; a caller that moves focus itself (a busy button, a concern card) keeps its choice.
 */
import { useEffect, useId, useRef, type ReactNode } from "react";

export function ConfirmDialog({ open, title, body, confirmLabel, cancelLabel, onConfirm, onCancel, children, confirmTone = "danger", confirmDisabled = false, initialFocus, returnFocus }: {
  readonly initialFocus?: string;
  readonly returnFocus?: string;
  readonly open: boolean;
  readonly title: string;
  readonly body: ReactNode;
  /** Optional fields between the text and the buttons (the spec Reject dialog's reason, AIC-181). */
  readonly children?: ReactNode;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  /** danger (default) for a destructive choice; go (gold) where confirming is the viewer's next move, e.g. Approve. */
  readonly confirmTone?: "danger" | "go";
  /** The confirm button is disabled until the fields make sense (a required reason, AIC-251). */
  readonly confirmDisabled?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const safe = useRef<HTMLButtonElement>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const id = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      returnTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
      (initialFocus ? d.querySelector<HTMLElement>(initialFocus) : safe.current)?.focus();
    } else if (!open && d.open) {
      if (typeof d.close === "function") d.close();
      else d.removeAttribute("open");
      const back = returnTo.current;
      returnTo.current = null;
      setTimeout(() => {
        const at = document.activeElement;
        const lost = !at || at === document.body || !at.isConnected || d.contains(at);
        if (lost && back?.isConnected) (returnFocus ? document.querySelector<HTMLElement>(returnFocus) : back)?.focus();
      }, 0);
    }
  }, [open]);

  const cancel = () => {
    onCancel();
    const back = returnTo.current;
    returnTo.current = null;
    // After the dialog closes: the trigger is inert while the modal is open.
    setTimeout(() => (returnFocus ? document.querySelector<HTMLElement>(returnFocus) : back)?.focus(), 0);
  };

  return (
    <dialog
      ref={ref}
      className="ipd-dialog"
      aria-labelledby={`${id}-h`}
      aria-describedby={`${id}-p`}
      onCancel={(e) => { e.preventDefault(); cancel(); }}
      onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); cancel(); } }}
      onClick={(e) => {
        // Backdrop = the pointer is outside the dialog's box (a click on its own padding also targets the dialog).
        if (e.target !== e.currentTarget) return;
        const r = e.currentTarget.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) return;
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) cancel();
      }}
    >
      {open ? (
        <>
          <h2 id={`${id}-h`}>{title}</h2>
          <p id={`${id}-p`}>{body}</p>
          {children}
          <div className="ipd-dialog-acts">
            <button ref={safe} type="button" className="ipd-btn" onClick={cancel}>{cancelLabel}</button>
            <button type="button" className={`ipd-btn ipd-btn--${confirmTone}`} disabled={confirmDisabled} onClick={onConfirm}>{confirmLabel}</button>
          </div>
        </>
      ) : null}
    </dialog>
  );
}
